
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { isAuthenticated, isSuperAdmin, logoutUser, getCurrentUser } from '@/utils/auth';
import { safeUserRole } from '@/utils/roleUtils';
import { Subscription } from '@/types/subscription';
import { generateMockSubscriptions } from '@/utils/mockData/subscriptionsMockData';
import { 
  filterSubscriptions, 
  sendTrialReminder, 
  updateSubscription 
} from '@/services/subscriptionService';

export const useSubscriptionManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = getCurrentUser();
      const userRole = currentUser?.role || '';
      
      if (!isAuthenticated() || !isSuperAdmin(safeUserRole(userRole))) {
        navigate('/login');
        return;
      }
      
      setUser(currentUser);
      
      // Load mock data
      const mockSubscriptions = generateMockSubscriptions();
      setSubscriptions(mockSubscriptions);
      setIsLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleViewOnboarding = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setOnboardingDialogOpen(true);
  };

  const handleSendTrialReminder = async (subscription: Subscription) => {
    try {
      const success = await sendTrialReminder(subscription);
      
      if (success) {
        // Update the subscription to mark reminder as sent
        const updatedSubscription = { ...subscription, trialReminderSent: true };
        const updatedSubscriptions = updateSubscription(subscriptions, updatedSubscription);
        
        setSubscriptions(updatedSubscriptions);
        
        toast({
          title: "Reminder Sent",
          description: `Trial expiry reminder sent to ${subscription.businessName}`,
        });
      } else {
        toast({
          title: "Failed to Send",
          description: "There was an error sending the trial expiry reminder",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error sending trial reminder:', error);
      toast({
        title: "Error",
        description: "Failed to send trial expiry reminder",
        variant: "destructive",
      });
    }
  };

  const handleChoosePlan = () => {
    navigate('/business-signup', { state: { fromSubscription: true } });
  };

  // Get filtered subscriptions based on search query
  const filteredSubscriptions = filterSubscriptions(subscriptions, searchQuery);

  return {
    isLoading,
    user,
    filteredSubscriptions,
    searchQuery,
    selectedSubscription,
    onboardingDialogOpen,
    handleLogout,
    handleSearch,
    handleViewOnboarding,
    handleSendTrialReminder,
    handleChoosePlan,
    setOnboardingDialogOpen
  };
};
